import React, { createContext, useContext, useEffect, useState } from "react";

import monitorApi from "./api/monitorApi";
import { SiteDataProviderPropsType } from "./SiteDataContextTypes";
import { AuthStateType, SiteDataContextTypeType } from "./SiteDataTypes";

const SiteDataContext = createContext<SiteDataContextTypeType | undefined>(
  undefined,
);

export const checkIsAuthenticated = (): boolean => {
  const token = localStorage.getItem("auth_token");
  const username = localStorage.getItem("auth_username");
  return !!(token && username);
};

export const SiteDataProvider: React.FC<SiteDataProviderPropsType> = ({
  children,
  siteData,
  setSiteData,
}) => {
  const [authState, setAuthState] = useState<AuthStateType>({
    isAuthenticated: false,
    user: null,
    token: null,
    isLoading: true,
  });

  const validateToken = async (): Promise<boolean> => {
    try {
      // Use the dedicated token check endpoint
      const response = await monitorApi.checkToken();
      // Check the 'valid' field in the response
      return response.data.valid === true;
    } catch (error: any) {
      // If the API call fails due to auth issues, token is invalid
      if (
        error.response &&
        (error.response.status === 401 || error.response.status === 403)
      ) {
        return false;
      }
      // For other errors (network issues, etc.), assume token is still valid
      // to avoid logging out users due to temporary network problems
      return true;
    }
  };

  const logout = (): void => {
    localStorage.removeItem("auth_token");
    localStorage.removeItem("auth_username");
    setAuthState({
      isAuthenticated: false,
      user: null,
      token: null,
      isLoading: false,
    });
  };

  useEffect(() => {
    const checkAuthentication = async () => {
      const username = localStorage.getItem("auth_username");

      if (username) {
        // Validate token with the API
        const isValid = await validateToken();

        if (isValid) {
          const token = localStorage.getItem("auth_token");
          setAuthState({
            isAuthenticated: true,
            user: { username },
            token,
            isLoading: false,
          });
        } else {
          // Token is invalid, clear auth state and redirect to login
          logout();
        }
      } else {
        setAuthState((prev) => ({
          ...prev,
          isLoading: false,
        }));
      }
    };

    checkAuthentication();
  }, []);

  return (
    <SiteDataContext.Provider
      value={{
        siteData,
        setSiteData,
        authState,
        setAuthState,
        logout,
      }}
    >
      {children}
    </SiteDataContext.Provider>
  );
};

export const useSiteData = (): SiteDataContextTypeType => {
  const context = useContext(SiteDataContext);
  if (context === undefined) {
    throw new Error("useSiteData must be used within a SiteDataProvider");
  }
  return context;
};
